import { useHubContext } from '@common/context/HubContext/HubContext';
import { generateRecipePackage } from '@common/recipeActions/saveRecipe';
import { KemuHubFunctions, SaveRecipeToFileArgs } from '@kemu-io/hs-types';
import { selectLogicMapperSettings, showGlobalNotification } from '@src/features/interface/interfaceSlice';
import { selectCurrentRecipe } from '@src/features/Workspace/workspaceSlice';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '.';
import { selectSignedUserProfile } from '@src/app/reducers/user/userSlice';

/**
 * Allows updating the contents of a recipe currently open from disk.
 * @returns a function to save the current recipe to disk
 */
const useUpdateRecipeFile = () => {
  const currentRecipe = useSelector(selectCurrentRecipe);
  const { profile: userProfile } = useSelector(selectSignedUserProfile);
  const logicMapperSettings = useSelector(selectLogicMapperSettings);
  const dispatch = useDispatch(); 
  const t = useTranslation('Recipe.SaveToFile');
  const { connector, online } = useHubContext();
  
  const saveCurrentRecipeToDisk = useCallback(async () => {
    const notifyError = (message: string) => {
      dispatch(showGlobalNotification({
        title: t('Error.Title'),
        message: message,
        type: 'error',
      }));
    }

    if(!online) {
      notifyError(t('Error.HubOffline'));
      return;
    }

    if(!currentRecipe.loadedFromDisk || !currentRecipe.uuid) {
      throw new Error('Recipe is not loaded from disk');
    }

    if(!currentRecipe.poolId) {
      throw new Error('Recipe pool id is not set');
    }

    if(!userProfile) {
      throw new Error('User profile not loaded yet');
    }

    const { contents } = await generateRecipePackage({
      recipePoolId: currentRecipe.poolId,
      name: currentRecipe.name,
      authorInfo: {
        id: userProfile.id,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName || '',
      },
      saveGlobalVariableValues: logicMapperSettings.saveGlobalVariableValues,
    });

    const encoder = new TextEncoder();
    const contentsBuffer = encoder.encode(contents).buffer;

    const args: SaveRecipeToFileArgs = [{
      recipeUuid: currentRecipe.uuid,
      contents: contentsBuffer as ArrayBuffer,
    }];


    try {
      await connector.executeHubFunction(KemuHubFunctions.SaveRecipeToFile, args, {
        timeout: 4000,
      });
      dispatch(showGlobalNotification({
        title: t('Success.Title'),
        message: t('Success.Message'),
        type: 'success',
      }));
    }catch(error) {
      console.error('Failed to save recipe to disk: ', error);
      dispatch(showGlobalNotification({
        title: t('Error.Title'),
        message: t('Error.Message'),
        type: 'error',
      }));
    }

    
  }, [currentRecipe, connector, logicMapperSettings.saveGlobalVariableValues]);

  return {
    saveCurrentRecipeToDisk,
  };
};

export default useUpdateRecipeFile;